import { FileCreateParams, FileObject, FilesApi } from '@citruscamps/citrus-client'
import translations from '../translations/en.json'
import { generateApiConfig } from '../utils/client-config'
import { useRequestHandler } from './useRequestHandler'
import { useToast } from './useToast'

export type FileUploadOptions = Pick<FileCreateParams, 'purpose'> &
  Partial<Pick<FileCreateParams, 'access'>>

interface IProps {
  context?: string
  onUpload?: (files: FileObject[]) => Promise<void>
}

interface IUploadFiles {
  handleUpload: (file: File, options?: FileUploadOptions) => Promise<FileObject>
  handleBulkUpload: (file: File[], options?: FileUploadOptions) => Promise<FileObject[]>
}

export const useUploadFiles = ({ context, onUpload }: IProps): IUploadFiles => {
  const { requestHandler } = useRequestHandler()
  const { setToast } = useToast()
  const handleUpload = async (
    file: File,
    { purpose = 'other', access }: FileUploadOptions = { purpose: 'other' },
  ): Promise<FileObject> => {
    try {
      const fileClient = new FilesApi(generateApiConfig())
      const formData = new FormData()
      formData.set('file', file)
      formData.set('purpose', purpose)
      if (access) formData.set('access', access)
      if (context) formData.set('context', context)
      const data = await requestHandler<FileObject>(() =>
        fileClient.createFile(
          {
            purpose,
            file,
          },
          { body: formData },
        ),
      )
      await onUpload?.([data])
      return data
    } catch (e: any) {
      setToast({
        header: (translations as any)['error.upload_image_failed'],
        message: e.message || (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
      throw e
    }
  }
  const handleBulkUpload = async (
    files: File[],
    options: FileUploadOptions = { purpose: 'other' },
  ): Promise<FileObject[]> => {
    try {
      const data = await requestHandler<FileObject[]>(
        async () => {
          const list = []
          for (const file of files) {
            const item = await handleUpload(file, options)
            list.push(item)
          }
          return list
        },
        {
          toast: {
            setToast,
          },
        },
      )
      await onUpload?.(data)
      return data
    } catch (e: any) {
      setToast({
        header: (translations as any)['error.upload_image_failed'],
        message: e.message || (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
      throw e
    }
  }
  return { handleUpload, handleBulkUpload }
}
